import { MAXIMUM_DATA_SIZE_MB } from "@/const";
import axios, { isAxiosError, AxiosError, AxiosResponse } from "axios";

type ApiConfig = {
  URL_LOGOUT: string;
  baseURL: string;
  API_KEY: string;
};

const baseURL = (() => {
  if (process.env.API_BASE_URL) return process.env.API_BASE_URL;
  switch (process.env.APP_ENV) {
    case "qa":
      return "https://api.qa-sheets.algo-artis.ai/v1";
    case "staging":
      return "https://api.stg-sheets.algo-artis.ai/v1";
    case "production":
      return "https://api.sheets.algo-artis.ai/v1";
    default:
      return "https://api.dev-sheets.algo-artis.ai/v1";
  }
})();

export const isLocalApiServer =
  /^https?:\/\/(localhost|127\.0\.0\.1)(:|\/|$)/.test(baseURL);

export const API_CONFIG = (() => {
  const defaultConfig: ApiConfig = (() => {
    switch (process.env.APP_ENV) {
      case "qa": {
        return {
          URL_LOGOUT: `https://account.qa-sheets.algo-artis.ai/v2/logout?returnTo=${location.origin}&client_id=p2ZvKkreaOZHsM9CqIPbvlvyUoaQpiHp`,
          baseURL,
          API_KEY: "AIzaSyB3wq_omAZE78uU-QJSSv9rgCysen55F6s",
        };
      }
      case "staging": {
        return {
          URL_LOGOUT: `https://account.stg-sheets.algo-artis.ai/v2/logout?returnTo=${location.origin}&client_id=vbPULwGrR805Yjx89Q9H2n84u6opTySK`,
          baseURL,
          API_KEY: "AIzaSyCOA7UNxHYCLHjSr1vQGUSIxlCN6cVXnFc",
        };
      }
      case "production": {
        return {
          URL_LOGOUT: `https://account.sheets.algo-artis.ai/v2/logout?returnTo=${location.origin}&client_id=MtJnFJ7h1XjdyMuknUVupGWZPo5Tfxrj`,
          baseURL,
          API_KEY: "AIzaSyDe56NyJqk60i1CIui4fP89wi5liwGzzMc",
        };
      }
      default: {
        return {
          URL_LOGOUT: `https://account.dev-sheets.algo-artis.ai/v2/logout?returnTo=${location.origin}&client_id=dX9OcxQiFz59V9hgLZuSY2tamvxgatlS`,
          baseURL,
          API_KEY: "AIzaSyCaxeMwNkHNLUMvNP5qku8KCsoNvIHV0Ao",
        };
      }
    }
  })();

  return {
    ...defaultConfig,
    URL_LOGOUT: process.env.API_LOGOUT_URL || defaultConfig.URL_LOGOUT,
    API_KEY: process.env.API_KEY || defaultConfig.API_KEY,
  };
})();

export const API_CLIENT = axios.create({
  baseURL,
});

export const API_CLIENT_WITHOUT_IDTOKEN = axios.create({
  baseURL,
});

function axiosResponse(response: AxiosResponse<any, any>) {
  return response;
}

const buildErrorMsgUrl = (error: AxiosError): string => {
  const url = error.config?.url ?? "";
  if (/^([a-z][a-z\d+\-.]*:)?\/\//i.test(url)) return url;
  return (error.config?.baseURL?.replace(/\/$/, "") ?? "") + url;
};

async function axiosErrorReject(error: any) {
  // responseType: "blob" のリクエストではエラー時も response.data が Blob のまま渡るため、
  // JSON としてパースし直して後段の getAPIErrorDetailOrMessage が detail を読めるようにする
  if (isAxiosError(error) && error.response?.data instanceof Blob) {
    try {
      const text = await error.response.data.text();
      if (text) {
        error.response.data = JSON.parse(text);
      }
    } catch {
      // JSON でない (octet-stream 等) 場合は Blob のまま残す
    }
  }

  // レスポンスエラーではなくaxiosのエラーの場合の対応
  if (isAxiosError(error)) {
    const { params, headers } = error.config ?? {
      params: null,
      headers: null,
    };
    const headersForErrorMsg = { ...headers };
    // NOTE: headersにはAPIキーやアクセストークンが含まれるため、非表示
    if (headersForErrorMsg) {
      delete headersForErrorMsg["X-API-KEY"];
      delete headersForErrorMsg["Authorization"];
      delete headersForErrorMsg["X-Forwarded-Authorization"];
    }
    error.message += ` [ ${buildErrorMsgUrl(error)}, ${JSON.stringify({ params, headers: headersForErrorMsg })}]`;
  }
  return Promise.reject(error);
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
// NOTE: requestに関するinterceptorsはAppAuthProviderで設定
API_CLIENT.interceptors.response.use(axiosResponse, axiosErrorReject);

// idtokenやAPIキーをリクエスト前に取得して付与する前処理
API_CLIENT_WITHOUT_IDTOKEN.interceptors.request.use(async (config) => {
  // APIキーをヘッダーに追加
  config.headers["X-API-KEY"] = API_CONFIG.API_KEY;
  return config;
});

API_CLIENT_WITHOUT_IDTOKEN.interceptors.response.use(
  axiosResponse,
  axiosErrorReject
);

const applyCompression = async (formData: FormData): Promise<void> => {
  if (!("CompressionStream" in window)) return;
  const filesMap: { [key: string]: File[] } = {};
  for (const [key, value] of formData.entries()) {
    if (value instanceof File) {
      if (!filesMap[key]) filesMap[key] = [];
      filesMap[key].push(value);
    }
  }
  for (const key in filesMap) {
    const newValues: FormDataEntryValue[] = [];
    for (const etcValue of formData
      .getAll(key)
      .filter((v) => !(v instanceof File))) {
      newValues.push(etcValue);
    }
    for (const file of filesMap[key]) {
      if (file.type === "application/json" || file.type.startsWith("text/")) {
        try {
          const cs = new CompressionStream("gzip");
          {
            const writer = cs.writable.getWriter();
            writer.write(await file.arrayBuffer());
            writer.close();
          }
          const compressedBlob = await new Response(cs.readable).blob();
          const compressedFile = new File([compressedBlob], file.name, {
            type: "application/gzip",
          });
          newValues.push(compressedFile);
          continue;
        } catch (e) {
          console.warn(e);
        }
      }
      newValues.push(file);
    }
    formData.delete(key);
    for (const value of newValues) {
      formData.append(key, value);
    }
  }
  return;
};
const computeTotalFileSize = (formData: FormData): number => {
  let totalSize = 0;
  for (const value of formData.values()) {
    if (value instanceof File) {
      totalSize += value.size;
    }
  }
  return totalSize;
};

API_CLIENT.interceptors.request.use(async (config) => {
  if (config.data instanceof FormData) {
    await applyCompression(config.data);
    const totalSize = computeTotalFileSize(config.data);
    if (totalSize >= MAXIMUM_DATA_SIZE_MB * 1024 * 1024) {
      return Promise.reject(
        new Error(
          `ファイルサイズが大きすぎます。合計${MAXIMUM_DATA_SIZE_MB}MB未満にしてください。`
        )
      );
    }
  }
  return config;
});
